import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { BillingType, Company, MigrationStatus, useCompanies } from '../hooks/useCompanies';
import {
  CreateCompanyInput,
  useCreateCompanyMutation,
} from 'src/secret-menu/hooks/useCreateCompanyMutation';
import { useUpdateCompanyMutation } from 'src/secret-menu/hooks/useUpdateCompanyMutation';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

const formatPenceToPounds = (pence: number | null | undefined): string => {
  if (pence === null || pence === undefined) return '-';
  return `£${(pence / 100).toFixed(2)}`;
};

const billingTypeOptions = [
  { value: '', label: 'None' },
  { value: BillingType.FIXED, label: 'Fixed' },
  { value: BillingType.PER_UNIT, label: 'Per Unit' },
];

type EditFormValues = {
  name: string;
  finio_firm_name: string;
  fca_firm_reference_numbers: string;
  is_internal: boolean;
  billing_type: BillingType | '';
  billing_amount: number | null;
};

export default function CompaniesTab() {
  const { companies, isLoading, isError } = useCompanies();
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] =
    useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const { createCompany, isLoading: isCreating } = useCreateCompanyMutation();
  const { updateCompany, isLoading: isUpdating } = useUpdateCompanyMutation();

  const createForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      fca_firm_reference_number: '',
      billing_type: '' as BillingType | '',
      billing_amount: null as number | null,
    },
    validate: {
      name: value =>
        value.length > 50
          ? 'Name must be at most 50 characters'
          : !/[A-Za-z0-9]/.test(value)
            ? 'Name must contain at least one alphanumeric character'
            : null,
    },
  });

  const editForm = useForm<EditFormValues>({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      finio_firm_name: '',
      fca_firm_reference_numbers: '',
      is_internal: false,
      billing_type: '' as BillingType | '',
      billing_amount: null as number | null,
    },
    validate: {
      name: value =>
        value.length > 50
          ? 'Name must be at most 50 characters'
          : !/[A-Za-z0-9]/.test(value)
            ? 'Name must contain at least one alphanumeric character'
            : null,
    },
  });

  if (isLoading) {
    return <Text>Loading...</Text>;
  }

  if (isError) {
    return <Text>Failed to load companies</Text>;
  }

  const handleCreateCompany = async (values: {
    name: string;
    fca_firm_reference_number: string;
    billing_type: BillingType | '';
    billing_amount: number | null;
  }) => {
    try {
      const input: CreateCompanyInput = {
        name: values.name,
        fca_firm_reference_number: values.fca_firm_reference_number,
        billing_type: values.billing_type || null,
        billing_amount: values.billing_amount,
      };
      await createCompany(input);
      createForm.reset();
      closeCreateModal();
    } catch (error) {
      notifyAndLogError(
        'Failed to create company',
        'An error occurred while creating the company.',
        error,
      );
    }
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    editForm.setValues({
      name: company.name,
      finio_firm_name: company.finioFirmName || '',
      fca_firm_reference_numbers:
        company.fcaFirmReferenceNumbers?.map(n => n.number).join(', ') || '',
      is_internal: company.isInternal,
      billing_type: company.billingType || '',
      billing_amount: company.billingAmount ?? null,
    });
    openEditModal();
  };

  const handleUpdateCompany = async (values: EditFormValues) => {
    if (!selectedCompany) return;

    try {
      const fcaNumbers = values.fca_firm_reference_numbers
        .split(',')
        .map(n => n.trim())
        .filter(n => n.length > 0)
        .map(number => {
          const existing = selectedCompany.fcaFirmReferenceNumbers?.find(f => f.number === number);
          return { id: existing?.id, number };
        });

      await updateCompany({
        companyId: selectedCompany.id,
        name: values.name,
        finioFirmName: values.finio_firm_name || null,
        fcaFirmReferenceNumbers: fcaNumbers,
        isInternal: values.is_internal,
        billingType: values.billing_type || null,
        billingAmount:
          !values.billing_amount && values.billing_amount !== 0 ? null : values.billing_amount,
      });
      editForm.reset();
      closeEditModal();
      setSelectedCompany(null);
    } catch (error) {
      notifyAndLogError(
        'Failed to update company',
        'An error occurred while updating the company.',
        error,
      );
    }
  };

  const getMigrationStatusColor = (status: MigrationStatus) => {
    switch (status) {
      case MigrationStatus.COMPLETED:
        return 'green';
      case MigrationStatus.IN_PROGRESS:
        return 'blue';
      case MigrationStatus.FAILED:
        return 'red';
      case MigrationStatus.NOT_STARTED:
      default:
        return 'gray';
    }
  };

  const getMigrationStatusLabel = (status: MigrationStatus) => {
    switch (status) {
      case MigrationStatus.NOT_STARTED:
        return 'Not Started';
      case MigrationStatus.IN_PROGRESS:
        return 'In Progress';
      case MigrationStatus.COMPLETED:
        return 'Completed';
      case MigrationStatus.FAILED:
        return 'Failed';
      default:
        return status;
    }
  };

  const getBillingTypeLabel = (billingType: BillingType | null | undefined): string => {
    if (!billingType) return '-';
    switch (billingType) {
      case BillingType.FIXED:
        return 'Fixed';
      case BillingType.PER_UNIT:
        return 'Per Unit';
      default:
        return billingType;
    }
  };

  const rows = companies?.map((company: Company) => (
    <Table.Tr key={company.id}>
      <Table.Td>{company.name}</Table.Td>
      <Table.Td>{company.tenantName}</Table.Td>
      <Table.Td>{company.fcaFirmReferenceNumbers?.map(n => n.number).join('  |  ')}</Table.Td>
      <Table.Td>{company.clientsCount}</Table.Td>
      <Table.Td>
        <Badge color={getMigrationStatusColor(company.migrationStatus)}>
          {getMigrationStatusLabel(company.migrationStatus)}
        </Badge>
      </Table.Td>
      <Table.Td>{company.isInternal ? 'Yes' : 'No'}</Table.Td>
      <Table.Td>{getBillingTypeLabel(company.billingType)}</Table.Td>
      <Table.Td>
        {company.billingAmount !== null && company.billingAmount !== undefined
          ? `£${Number(company.billingAmount).toFixed(2)}`
          : '-'}
      </Table.Td>
      <Table.Td>{formatPenceToPounds(company.monthlyBillPence)}</Table.Td>
      <Table.Td>
        <Button size="xs" variant="light" onClick={() => handleEditCompany(company)}>
          Edit
        </Button>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Box>
      <Modal onClose={closeCreateModal} opened={createModalOpened} title="Create company">
        {isCreating ? (
          <Loader id="loader" />
        ) : (
          <form onSubmit={createForm.onSubmit(values => handleCreateCompany(values))}>
            <TextInput
              required
              label="Company name"
              placeholder="eg. Afternoon limited"
              key={createForm.key('name')}
              {...createForm.getInputProps('name')}
            />
            <TextInput
              required
              label="FCA Firm Reference Number"
              placeholder="eg. 1234567"
              key={createForm.key('fca_firm_reference_number')}
              {...createForm.getInputProps('fca_firm_reference_number')}
            />
            <Select
              mt="md"
              label="Billing Type"
              placeholder="Select billing type"
              data={billingTypeOptions}
              key={createForm.key('billing_type')}
              {...createForm.getInputProps('billing_type')}
            />
            <NumberInput
              mt="md"
              label="Billing Amount (£)"
              placeholder="eg. 10.00"
              decimalScale={2}
              min={0}
              key={createForm.key('billing_amount')}
              {...createForm.getInputProps('billing_amount')}
            />
            <Button mt={'md'} type="submit">
              Submit
            </Button>
          </form>
        )}
      </Modal>

      <Modal
        onClose={closeEditModal}
        opened={editModalOpened}
        title={`Edit Company - ${selectedCompany?.name ?? ''}`}
        size="lg"
      >
        {isUpdating ? (
          <Loader id="edit-loader" />
        ) : (
          <form onSubmit={editForm.onSubmit(values => handleUpdateCompany(values))}>
            <Stack gap="md">
              <TextInput
                required
                label="Company name"
                placeholder="eg. Afternoon limited"
                key={editForm.key('name')}
                {...editForm.getInputProps('name')}
              />
              <TextInput
                label="FINIO Firm Name"
                placeholder="eg. FINIO firm name"
                key={editForm.key('finio_firm_name')}
                {...editForm.getInputProps('finio_firm_name')}
              />
              <TextInput
                label="FCA Firm Reference Numbers"
                description="Comma-separated list of 6-7 digit numbers"
                placeholder="eg. 123456, 1234567"
                key={editForm.key('fca_firm_reference_numbers')}
                {...editForm.getInputProps('fca_firm_reference_numbers')}
              />
              <Checkbox
                label="Internal Company"
                key={editForm.key('is_internal')}
                {...editForm.getInputProps('is_internal', { type: 'checkbox' })}
              />
              <Select
                label="Billing Type"
                placeholder="Select billing type"
                data={billingTypeOptions}
                key={editForm.key('billing_type')}
                {...editForm.getInputProps('billing_type')}
              />
              <NumberInput
                label="Billing Amount (£)"
                placeholder="eg. 10.00"
                decimalScale={2}
                min={0}
                key={editForm.key('billing_amount')}
                {...editForm.getInputProps('billing_amount')}
              />
              <Group>
                <Button type="submit">Save</Button>
                <Button variant="light" onClick={closeEditModal}>
                  Cancel
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>

      <Button onClick={openCreateModal}>Create</Button>
      <Table mt={'md'}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Tenant name</Table.Th>
            <Table.Th>FRN</Table.Th>
            <Table.Th>Clients count</Table.Th>
            <Table.Th>Migration Status</Table.Th>
            <Table.Th>Internal</Table.Th>
            <Table.Th>Billing Type</Table.Th>
            <Table.Th>Billing Amount</Table.Th>
            <Table.Th>Monthly Bill</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
    </Box>
  );
}
